import { useState, RefObject } from "react";
import { Share2, Download, Loader2 } from "lucide-react";
import { toPng } from "html-to-image";
import { toast } from "@/hooks/use-toast";

interface ShareButtonProps {
  slideRef: RefObject<HTMLDivElement>;
  slideIndex: number;
  onShare?: () => void;
}

const ShareButton = ({ slideRef, slideIndex, onShare }: ShareButtonProps) => {
  const [isCapturing, setIsCapturing] = useState(false);

  const captureAndShare = async () => {
    if (!slideRef.current || isCapturing) return;

    setIsCapturing(true);

    try {
      // Capture the slide as PNG using html-to-image
      const dataUrl = await toPng(slideRef.current, {
        backgroundColor: "#0B1B23",
        pixelRatio: 2,
        cacheBust: true,
      });

      // Convert data URL to blob
      const response = await fetch(dataUrl);
      const blob = await response.blob();

      const file = new File([blob], `nimi-wrapped-2025-${slideIndex + 1}.png`, {
        type: "image/png",
      });

      // Check if Web Share API with files is supported (mobile)
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: "My Nimi 2025 Wrapped",
          text: "Check out my year in art! 🎨",
        });
        onShare?.();
      } else {
        // Desktop fallback: download the image
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `nimi-wrapped-2025-${slideIndex + 1}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        toast({
          title: "Image downloaded",
          description: "Your Wrapped screenshot has been saved.",
        });
        onShare?.();
      }
    } catch (error) {
      // User cancelled share or error occurred
      if ((error as Error).name !== "AbortError") {
        console.error("Share error:", error);
        toast({
          title: "Share failed",
          description: "Unable to share the image. Please try again.",
          variant: "destructive",
        });
      }
    } finally {
      setIsCapturing(false);
    }
  };

  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        captureAndShare();
      }}
      disabled={isCapturing}
      className="absolute top-4 left-4 z-20 p-2 rounded-full bg-background/10 hover:bg-background/20 transition-colors disabled:opacity-50"
      aria-label="Share this slide"
    >
      {isCapturing ? (
        <Loader2 className="w-5 h-5 text-background animate-spin" />
      ) : navigator.canShare ? (
        <Share2 className="w-5 h-5 text-background" />
      ) : (
        <Download className="w-5 h-5 text-background" />
      )}
    </button>
  );
};

export default ShareButton;
